import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { startSubscriptionCheckout } from '../api/stripeApi'; // Importiere Stripe API Funktion
import logger from '../utils/logger';
import { mapErrorToUserMessage } from '../utils/userErrorMessage';
import { BrandLogo, FaeBillMark } from '../components/common/BrandLogo';

const VerifyEmailPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // Um die E-Mail aus dem State zu holen
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false); // Neuer State für Checkout

  // E-Mail aus dem Route State holen (wird von RegisterPage übergeben)
  const email = location.state?.email || currentUser?.email;
  // PlanID aus dem State holen
  const planId = location.state?.plan as string | undefined;
  // Marketing-Einwilligung aus dem State holen
  const acceptedMarketing = location.state?.acceptedMarketing as boolean | undefined;

  // Funktion zur Initiierung des Checkouts
  const initiateCheckoutAndRedirect = useCallback(async (planIdToCheckout: string) => {
    if (isCheckingOut) return; // Verhindere Doppelklick
    setIsCheckingOut(true);
    setError(null);
    // Ändere die Nachricht, um den Benutzer zu informieren
    setMessage("E-Mail erfolgreich verifiziert! Leite zum Bezahlvorgang weiter...");
    try {
      logger.info({ planId: planIdToCheckout }, 'starting checkout');
      const checkoutUrl = await startSubscriptionCheckout(planIdToCheckout);
      // Weiterleitung zu Stripe
      window.location.assign(checkoutUrl);
      // setLoading/setIsCheckingOut muss hier nicht auf false gesetzt werden, da wir wegnavigieren
    } catch (checkoutError: any) {
      logger.error(checkoutError, 'stripe checkout error');
      setError(mapErrorToUserMessage(checkoutError, t, { fallbackKey: 'errors.payment.checkoutFailed' }));
      setMessage(null); // Erfolgsmeldung löschen
      setIsCheckingOut(false); // Checkout-Status bei Fehler zurücksetzen
    }
  }, [isCheckingOut]); // Abhängigkeit hinzugefügt

  // Prüfung des Verifizierungsstatus und Weiterleitungslogik
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return; // Frühzeitiger Ausstieg, wenn kein Benutzer vorhanden
    }

    // Wenn bereits verifiziert, entscheide, wohin es geht
    if (currentUser.emailVerified) {
      if (planId) {
        // Verifiziert und planId vorhanden -> Checkout starten
        initiateCheckoutAndRedirect(planId);
      } else {
        // Verifiziert und keine planId -> zum Dashboard
        navigate('/dashboard', { replace: true });
      }
    } 
    // Kein 'else' nötig, Benutzer bleibt auf der Seite, wenn nicht verifiziert
  }, [currentUser, navigate, planId, initiateCheckoutAndRedirect]); // Abhängigkeiten hinzugefügt

  const handleResendVerification = async () => {
    if (!currentUser) {
      setError('Kein Benutzer angemeldet.');
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      await sendEmailVerification(currentUser);
      setMessage('Verifizierungs-E-Mail erneut gesendet. Bitte überprüfen Sie Ihren Posteingang (und Spam-Ordner).');
    } catch (err: any) {
      logger.error(err, 'error resending verification email');
      setError('Fehler beim Senden der E-Mail. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualCheck = async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
        await currentUser.reload(); // Lädt die Benutzerdaten neu von Firebase
        // Prüfe Verifizierungsstatus *nach* dem Neuladen
        if (currentUser.emailVerified) {
            if (planId) {
                 // Verifiziert und Plan vorhanden -> Checkout starten
                initiateCheckoutAndRedirect(planId);
                // setLoading muss hier nicht auf false gesetzt werden, da wir wegnavigieren
            } else {
                // Verifiziert und kein Plan -> zum Dashboard
                setMessage("E-Mail erfolgreich verifiziert! Sie werden zum Dashboard weitergeleitet...");
                setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
                // Setze Loading auf false, wenn die Weiterleitung zum Dashboard erfolgt
                setLoading(false); 
            }
        } else {
            setError("E-Mail ist noch nicht verifiziert. Bitte überprüfen Sie Ihren Posteingang oder senden Sie die E-Mail erneut.");
            setLoading(false);
        }
    } catch (err) {
        logger.error(err, 'error reloading user data');
        setError("Fehler beim Überprüfen des Verifizierungsstatus.");
        setLoading(false);
    }
    // Kein finally mehr nötig, da setLoading(false) in den Zweigen behandelt wird
  };

  const handleLogout = async () => {
      try {
          await signOut(auth);
          navigate('/login');
      } catch (error) {
          logger.error(error, 'logout error');
          setError("Fehler beim Ausloggen.");
      }
  };

  if (!currentUser) {
    // Wird kurz angezeigt, bevor der useEffect-Redirect greift
    return (
      <div className="min-h-screen flex items-center justify-center auth-bg">
        <div className="bg-surface-raised p-8 rounded-lg shadow-xl border border-hairline text-primary">
          <div className="animate-pulse flex justify-center">
            <FaeBillMark className="h-12 w-12 mb-4" />
          </div>
          <p className="text-secondary">Lade Benutzerdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center auth-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-4 left-4">
        <Link to="/" className="flex items-center">
          <BrandLogo className="h-10" />
        </Link>
      </div>
      
      <div className="bg-surface-raised p-8 rounded-lg shadow-xl w-full max-w-md text-center border border-hairline text-primary">
        <div className="flex justify-center mb-6">
          <FaeBillMark className="h-12 w-12" />
        </div>
        <h2 className="text-2xl font-bold mb-4 text-primary">E-Mail Bestätigung erforderlich</h2>
        
        {/* Hinweis über die E-Mails */}
        <div className="mb-6 bg-[var(--tone-accent-bg)] border border-[var(--tone-accent-border)] rounded-lg p-4 text-left">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-accent-ink flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div>
              {acceptedMarketing ? (
                <>
                  <p className="text-sm text-secondary font-medium mb-2">
                    Wir haben <strong className="text-accent-ink">zwei E-Mails</strong> an <strong>{email || 'Ihre Adresse'}</strong> gesendet:
                  </p>
                  <ol className="text-sm text-secondary list-decimal list-inside space-y-1 mb-3">
                    <li>Bestätigungs-E-Mail zur <strong>Aktivierung Ihres Kontos</strong></li>
                    <li>Bestätigungs-E-Mail für den <strong>Newsletter</strong></li>
                  </ol>
                  <p className="text-sm text-accent-ink font-medium">
                    ⚠️ Bitte klicken Sie in <strong>beiden</strong> E-Mails auf den Bestätigungslink!
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm text-secondary mb-2">
                    Wir haben eine Bestätigungs-E-Mail an <strong>{email || 'Ihre Adresse'}</strong> gesendet.
                  </p>
                  <p className="text-sm text-secondary">
                    Bitte klicken Sie auf den Link in der E-Mail, um Ihr Konto zu aktivieren.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Spam-Hinweis */}
        <div className="mb-6 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg p-3 text-left">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-[var(--tone-warning-fg)] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-[var(--tone-warning-fg)]">
              <strong>Tipp:</strong> Schauen Sie auch im <strong>Spam-Ordner</strong> nach, falls Sie die E-Mail(s) nicht finden.
            </p>
          </div>
        </div>

        {message && (
          <div className="mb-4 bg-[var(--tone-success-bg)] border-l-4 border-[var(--tone-success-border)] p-4 text-left">
            <p className="text-[var(--tone-success-fg)] text-sm">{message}</p>
          </div>
        )}
        
        {error && (
          <div className="mb-4 bg-[var(--tone-danger-bg)] border-l-4 border-[var(--tone-danger-border)] p-4 text-left">
            <p className="text-primary text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <button
            onClick={handleResendVerification}
            disabled={loading || isCheckingOut} // Deaktivieren bei Laden oder Checkout
            className="w-full bg-indigo-600 text-[#0C1322] py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Sende...' : isCheckingOut ? 'Leite weiter...' : 'Bestätigungs-E-Mail erneut senden'}
          </button>
           <button
            onClick={handleManualCheck}
            disabled={loading || isCheckingOut} // Deaktivieren bei Laden oder Checkout
            className="w-full bg-surface-inset text-primary py-2 px-4 rounded-md hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Prüfe...' : isCheckingOut ? 'Leite weiter...' : 'Ich habe meine E-Mail verifiziert'}
          </button>
          <button
            onClick={handleLogout}
            disabled={isCheckingOut} // Deaktivieren bei Checkout
            className="w-full bg-transparent border border-strong text-secondary py-2 px-4 rounded-md hover:bg-surface-inset focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 transition-colors"
          >
            Abmelden & zum Login
          </button>
        </div>

      </div>
    </div>
  );
};

export default VerifyEmailPage; 